<template>
  <div>
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="showError = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Success Snackbar -->
    <v-snackbar v-model="showSuccess" color="success" timeout="3000">
      {{ successMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="showSuccess = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Back Button Header -->
    <div
      style="background-color: #ffffff; border-radius: 4px; padding: 12px 20px; margin: 16px 24px 0 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center;">
      <div style="display: flex; align-items: center; gap: 10px;">
        <v-icon size="18" color="#555">mdi-view-dashboard-edit</v-icon>
        <span
          style="font-weight: 700; font-size: 15px; color: #444; letter-spacing: 0.5px; text-transform: uppercase;">EDIT
          DEALER DETAILS</span>
      </div>
      <v-btn variant="flat"
        style="background-color: #800080 !important; color: #ffffff !important; border-radius: 4px !important;"
        @click="goBack" :disabled="loading">
        CLOSE
      </v-btn>
    </div>

    <v-card class="pa-6 mx-6 custom_card_box mt-4" border="sm opacity-1">
      <v-form @submit.prevent="updateOutletData" ref="dealerForm" v-model="formValid">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="formData.company_name" label="Company/Institute Name" variant="outlined"
              density="comfortable" class="mb-4" :rules="[rules.required]" required />
            <v-text-field v-model="formData.dealer_name" label="Dealer Name" variant="outlined" density="comfortable"
              class="mb-4" :rules="[rules.required]" required />
            <v-text-field v-model="formData.dealer_code" label="Dealer Code" variant="outlined" density="comfortable"
              class="mb-4" :rules="[rules.required]" required />

            <!-- NEW: Dealer Type Field -->
            <v-select v-model="formData.dealer_type" :items="dealerTypes" label="Dealer Type" variant="outlined"
              density="comfortable" class="mb-4" :rules="[rules.required]" required clearable />

            <div class="d-flex align-center mb-4">
              <p class="text-subtitle-2 font-weight-bold mb-0">Phone Numbers</p>
              <v-spacer></v-spacer>
              <v-btn size="small" color="primary" variant="text" @click="addPhoneNumber"
                :disabled="phoneNumbers.length >= 2">
                + Add Number
              </v-btn>
            </div>

            <div v-if="phoneNumbers.length > 0">
              <div class="d-flex align-center mb-4" v-for="(phone, index) in phoneNumbers" :key="index">
                <v-icon size="small" class="mr-3 mt-n5">mdi-phone</v-icon>
                <div class="d-flex flex-grow-1 gap-2">
                  <v-text-field model-value="+94" label="Code" variant="outlined" density="comfortable"
                    class="country-code-select" hide-details readonly />
                  <v-text-field v-model="phone.mobile_number"
                    :label="index === 0 ? 'Primary Phone Number' : 'Secondary Phone Number'" variant="outlined"
                    density="comfortable" class="flex-grow-1" :rules="[rules.required, rules.phoneNumber]"
                    hide-details="auto" maxlength="9" @input="formatPhoneNumber(phone)" />
                </div>
                <v-btn icon color="error" variant="text" size="small" class="ml-2 mt-n5"
                  @click="removePhoneNumber(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else class="text-center text-grey pa-4 mb-4">
              No phone numbers added. Click "Add Number" to add a phone number.
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="d-flex align-center mb-4">
              <p class="text-subtitle-2 font-weight-bold mb-0">Company Details</p>
            </div>

            <div v-if="piyestraCompanies.length > 0">
              <div v-for="(company, index) in piyestraCompanies" :key="index" class="mb-4">
                <v-card variant="outlined" class="pa-3 mb-2">
                  <div class="d-flex align-center mb-2">
                    <v-card-title class="text-subtitle-2 font-weight-bold pa-0">Company {{ index + 1 }}</v-card-title>
                    <v-spacer></v-spacer>
                    <v-btn icon color="error" variant="text" size="small" @click="removeCompany(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                  <v-row dense>
                    <v-col cols="12">
                      <v-select v-model="company.company" :items="getCompanyOptions(index)" label="Company Name"
                        variant="outlined" density="comfortable" class="mb-2" :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="company.credit_limit_type" label="Credit Limit" variant="outlined"
                        density="comfortable" type="number" prefix="Rs." class="mb-2" :rules="[rules.requiredNumber]" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="company.credit_period_type" label="Credit Period (days)" variant="outlined"
                        density="comfortable" type="number" suffix="days" class="mb-2"
                        :rules="[rules.requiredNumber]" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="company.cash_limit_type" label="Cash Limit" variant="outlined"
                        density="comfortable" type="number" prefix="Rs." class="mb-2" :rules="[rules.requiredNumber]" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="company.cash_period_type" label="Cash Period (days)" variant="outlined"
                        density="comfortable" type="number" suffix="days" class="mb-2"
                        :rules="[rules.requiredNumber]" />
                    </v-col>
                    <!-- Display rep_code as read-only field -->
                    <!-- <v-col cols="12">
                      <v-text-field 
                        v-model="company.rep_code"
                        label="Rep Code" 
                        variant="outlined" 
                        density="comfortable" 
                        readonly
                        class="mb-2"
                        hint="Rep code is automatically assigned and cannot be edited"
                        persistent-hint
                      />
                    </v-col> -->
                  </v-row>
                </v-card>
              </div>
            </div>

            <v-btn color="primary" variant="text" @click="addCompany" class="mb-4" :disabled="!canAddCompany">
              <v-icon left>mdi-plus</v-icon> Add Company
            </v-btn>

            <!-- Warning message when no companies selected -->
            <div v-if="piyestraCompanies.length === 0" class="text-caption text-orange-darken-2 mt-2">
              ⚠️ At least one company must be selected
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <p class="text-subtitle-1 font-weight-bold mb-4">DEALER LOCATION</p>
        <p class="text-caption text-grey mb-3">Click on the map, drag the marker, or manually edit the coordinates to
          update
          the outlet's location.</p>

        <v-row>
          <v-col cols="12" md="6">
            <div class="map-container">
              <Map ref="map" :center="mapCenter" :zoom="15" style="height: 300px; width: 100%;"
                class="custom_card_box rounded-lg overflow-hidden">
                <Marker v-if="markerPosition" :icon="customIcon" :position="markerPosition" :draggable="true"
                  @dragend="onMarkerDragEnd" />
              </Map>
            </div>
            <div class="text-center mt-2">
              <v-chip v-if="markerPosition" color="primary" size="small">
                📍 Lat: {{ markerPosition.lat.toFixed(6) }} | Lng: {{ markerPosition.lng.toFixed(6) }}
              </v-chip>
              <v-chip v-else color="grey" size="small">
                📍 Click on map to set location
              </v-chip>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="formData.address" label="Address" variant="outlined" density="comfortable"
              class="mb-4" :rules="[rules.required]" required />
            <v-select v-model="formData.region_code" :items="uniqueRegionCodes" label="Region Code" variant="outlined"
              density="comfortable" class="mb-4" :rules="[rules.required]" required
              @update:modelValue="onRegionChange" />
            <v-select v-model="formData.area_code" :items="filteredAreaCodes" :loading="areaCodeLoading"
              label="Area Code" variant="outlined" density="comfortable" class="mb-4" :rules="[rules.required]"
              required />

            <v-row>
              <v-col cols="6">
                <v-text-field v-model="editableLatitude" label="Latitude" variant="outlined" density="comfortable"
                  type="number" step="any" class="mb-4" :rules="[rules.required, rules.latitude]"
                  @blur="updateMarkerFromCoords" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="editableLongitude" label="Longitude" variant="outlined" density="comfortable"
                  type="number" step="any" class="mb-4" :rules="[rules.required, rules.longitude]"
                  @blur="updateMarkerFromCoords" />
              </v-col>
            </v-row>
            <div class="text-caption text-grey">
              <v-icon icon="mdi-information" size="small"></v-icon>
              Edit coordinates manually or use the map above. Valid ranges: Latitude (-90 to 90), Longitude (-180 to
              180)
            </div>
          </v-col>
        </v-row>

        <div class="d-flex justify-end mt-6">
          <v-btn color="grey-darken-1" variant="outlined" class="mr-4" @click="cancelEdit">Cancel</v-btn>
          <v-btn type="submit" :loading="isSaving" :disabled="isSaving || loading"
            style="background-color: #800080; color: white;">
            Update Dealer
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import { Map, Marker } from '@fawmi/vue-google-maps';
import { getPermissions } from "@/plugins/permissions";

export default {
  name: "EditDealer",
  components: {
    Map,
    Marker,
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_API_BASE_URL,
      loading: true,
      isSaving: false,
      showError: false,
      showSuccess: false,
      errorMessage: '',
      successMessage: '',
      formValid: false,

      allAreasData: [],
      uniqueRegionCodes: [],
      filteredAreaCodes: [],
      areaCodeLoading: false,

      formData: {
        dealer_name: '',
        dealer_code: '',
        company_name: '',
        address: '',
        region_code: '',
        area_code: '',
        dealer_type: '', // NEW: Add dealer_type to formData
      },

      // Updated Dealer Types based on the provided image
      dealerTypes: [
        'Furniture & Electronic',
        'Furniture',
        'Electronic',
        'Hardware',
        'Super Center-Daxer',
        'Book Shop',
        'Delivery & Government Suppliers',
        'Non Transaction'
      ],

      phoneNumbers: [],
      piyestraCompanies: [],
      availablePiyestraCompanies: [
        'D.R.Home Appliances (Pvt) Ltd',
        'Piyestra Furniture (Pvt) Ltd',
        'D.R.Industries Electronics',
      ],

      mapCenter: { lat: 6.9271, lng: 79.8612 }, // Default Sri Lanka
      markerPosition: null,
      editableLatitude: '',
      editableLongitude: '',
      customIcon: {
        url: require('@/assets/ICONS/maker.png'), // Match the working page path
        scaledSize: { width: 35, height: 40 }
      },

      permissions: {},
      countryCodes: ['+94'],

      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'This field is required',
        requiredNumber: (v) => {
          if (v === null || v === undefined || String(v).trim() === '') {
            return 'This field is required';
          }
          return !isNaN(Number(v)) || 'Please enter a valid number';
        },
        phoneNumber: (v) => {
          if (!v) return true;
          // Updated to require exactly 9 digits
          const phoneRegex = /^\d{9}$/;
          return phoneRegex.test(v) || 'Please enter exactly 9 digits';
        },
        latitude: (v) => {
          if (v === null || v === undefined || v === '') return 'Latitude is required';
          const num = parseFloat(v);
          if (isNaN(num)) return 'Must be a valid number';
          if (num < -90 || num > 90) return 'Latitude must be between -90 and 90';
          return true;
        },
        longitude: (v) => {
          if (v === null || v === undefined || v === '') return 'Longitude is required';
          const num = parseFloat(v);
          if (isNaN(num)) return 'Must be a valid number';
          if (num < -180 || num > 180) return 'Longitude must be between -180 and 180';
          return true;
        }
      },

      abortController: null
    };
  },

  computed: {
    dealerId() {
      return this.$route.query.id;
    },

    hasCompletePhoneNumbers() {
      return this.phoneNumbers.length > 0 && this.phoneNumbers.every(phone => {
        const number = phone.mobile_number ? phone.mobile_number.trim() : '';
        return number !== '' && /^\d{9}$/.test(number);
      });
    },

    hasCompleteCompanies() {
      return this.piyestraCompanies.length > 0 && this.piyestraCompanies.every(company => {
        const requiredFields = [
          company.company,
          company.credit_limit_type,
          company.credit_period_type,
          company.cash_limit_type,
          company.cash_period_type
        ];
        return requiredFields.every(field => field !== null && field !== undefined && String(field).trim() !== '');
      });
    },

    hasLocation() {
      return !!this.markerPosition;
    },

    canAddCompany() {
      const selected = this.piyestraCompanies
        .map(company => company.company)
        .filter(Boolean);
      const uniqueSelected = new Set(selected);
      return uniqueSelected.size < this.availablePiyestraCompanies.length;
    },

    canSubmit() {
      return this.formValid && this.hasCompletePhoneNumbers && this.hasCompleteCompanies && this.hasLocation;
    }
  },

  watch: {
    dealerId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchDealerData();
        } else {
          this.showErrorNotification('No dealer ID provided');
          this.loading = false;
        }
      }
    },
    // Sync marker position to editable text fields
    markerPosition: {
      handler(newPos) {
        if (newPos) {
          this.editableLatitude = newPos.lat.toString();
          this.editableLongitude = newPos.lng.toString();
        } else {
          this.editableLatitude = '';
          this.editableLongitude = '';
        }
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    const dashboardId = 9;
    this.permissions = getPermissions(dashboardId);
    this.fetchAreasData();

    // Initialize map when component is mounted - similar to working page
    if (this.$refs.map) {
      this.$refs.map.$mapPromise.then(map => {
        console.log('Map loaded successfully');
        this.initMapEvents(map);
      }).catch(err => {
        console.log('Map not available:', err);
      });
    }
  },

  methods: {
    goBack() {
      // Navigate back to the previous page or dealer list
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/dealer-list');
      }
    },

    async fetchAreasData() {
      this.areaCodeLoading = true;
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL;
        const response = await axios.get(`${baseUrl}/api/area-allList`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data) {
          this.allAreasData = response.data.data;

          const regionSet = new Set();
          this.allAreasData.forEach(area => {
            if (area.region_code) {
              regionSet.add(area.region_code);
            }
          });
          this.uniqueRegionCodes = Array.from(regionSet).sort();

          if (this.formData.region_code) {
            this.onRegionChange(this.formData.region_code, true);
          }
        } else {
          console.error('Failed to load areas data:', response.data.message);
          this.uniqueRegionCodes = [];
        }
      } catch (err) {
        console.error('Error fetching areas data:', err);
        this.showErrorNotification('Failed to load region and area data');
        this.uniqueRegionCodes = [];
      } finally {
        this.areaCodeLoading = false;
      }
    },

    onRegionChange(selectedRegion, isInitialLoad = false) {
      if (!isInitialLoad) {
        this.formData.area_code = null;
      }

      if (!selectedRegion) {
        this.filteredAreaCodes = [];
        return;
      }

      this.areaCodeLoading = true;

      const areasForRegion = this.allAreasData.filter(area =>
        area.region_code === selectedRegion
      );

      this.filteredAreaCodes = areasForRegion.map(area => ({
        title: area.area_code,
        value: area.area_code,
        description: area.description
      })).sort((a, b) => a.title.localeCompare(b.title));

      this.areaCodeLoading = false;

      if (!isInitialLoad && this.filteredAreaCodes.length === 1) {
        this.formData.area_code = this.filteredAreaCodes[0].value;
        this.showSuccessMessage(`Auto-selected area code: ${this.formData.area_code}`);
      }
    },

    async fetchDealerData() {
      // Cancel previous request if exists
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      const token = localStorage.getItem('token');
      const dealerId = this.dealerId;

      if (!token) {
        this.showErrorNotification('Authentication token not found');
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        const response = await axios.get(
          `${this.baseURL}/api/getDealerById/${dealerId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: this.abortController.signal
          }
        );

        if (response.data && response.data.success && response.data.data) {
          this.populateFormData(response.data.data);
        } else {
          throw new Error(response.data?.message || 'Failed to fetch dealer data');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dealer data:", error);
          this.showErrorNotification(error.message || 'Failed to fetch dealer data');
        }
      } finally {
        this.loading = false;
        this.abortController = null;
      }
    },

    populateFormData(dealerData) {
      // Basic dealer information
      this.formData = {
        dealer_name: dealerData.dealer_name || '',
        dealer_code: dealerData.dealer_code || '',
        company_name: dealerData.company_name || '',
        address: dealerData.address || '',
        region_code: dealerData.region_code || '',
        area_code: dealerData.area_code || '',
        dealer_type: dealerData.dealer_type || '', // NEW: Populate dealer_type
      };

      if (this.formData.region_code && this.allAreasData.length > 0) {
        this.onRegionChange(this.formData.region_code, true);
      }

      // Phone numbers - using mobile_number and mobile_number2 fields
      const phones = [];
      if (dealerData.mobile_number) {
        phones.push({
          country_code: '+94',
          mobile_number: dealerData.mobile_number
        });
      }
      if (dealerData.mobile_number2) {
        phones.push({
          country_code: '+94',
          mobile_number: dealerData.mobile_number2
        });
      }
      this.phoneNumbers = phones.length > 0 ? phones : [];

      // Companies data - IMPORTANT: This includes the rep_code field
      const responseCompanies = Array.isArray(dealerData.companies)
        ? dealerData.companies
        : (Array.isArray(dealerData.piyestra_company) ? dealerData.piyestra_company : []);

      if (responseCompanies.length > 0) {
        this.piyestraCompanies = responseCompanies.map(company => ({
          company: company.company || '',
          credit_limit_type: company.credit_limit_type !== null ? String(company.credit_limit_type) : '',
          credit_period_type: company.credit_period_type !== null ? String(company.credit_period_type) : '',
          cash_limit_type: company.cash_limit_type !== null ? String(company.cash_limit_type) : '',
          cash_period_type: company.cash_period_type !== null ? String(company.cash_period_type) : '',
          rep_code: company.rep_code || ''  // Preserve the rep_code from backend
        }));
      } else {
        this.piyestraCompanies = [];
      }

      // Set map marker if coordinates available - using lat and lng from API
      if (dealerData.lat && dealerData.lng) {
        const lat = parseFloat(dealerData.lat);
        const lng = parseFloat(dealerData.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.mapCenter = { lat, lng };
          this.markerPosition = { lat, lng };
          this.editableLatitude = lat.toString();
          this.editableLongitude = lng.toString();
        }
      }
    },

    initMapEvents(map) {
      // Add click listener to map for single marker selection
      map.addListener('click', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        this.markerPosition = {
          lat: newLat,
          lng: newLng
        };
        // Update text fields immediately
        this.editableLatitude = newLat.toString();
        this.editableLongitude = newLng.toString();
      });

      // If marker exists, center map on it
      if (this.markerPosition) {
        map.setCenter(this.markerPosition);
        map.setZoom(15);
      }
    },

    onMarkerDragEnd(event) {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      this.markerPosition = {
        lat: newLat,
        lng: newLng
      };
      // Update text fields immediately
      this.editableLatitude = newLat.toString();
      this.editableLongitude = newLng.toString();
    },

    updateMarkerFromCoords() {
      // Validate and update marker position from text fields
      const latValid = this.rules.latitude(this.editableLatitude) === true;
      const lngValid = this.rules.longitude(this.editableLongitude) === true;

      if (latValid && lngValid && this.editableLatitude !== '' && this.editableLongitude !== '') {
        const newLat = parseFloat(this.editableLatitude);
        const newLng = parseFloat(this.editableLongitude);

        // Only update if coordinates have changed
        if (!this.markerPosition ||
          Math.abs(this.markerPosition.lat - newLat) > 0.000001 ||
          Math.abs(this.markerPosition.lng - newLng) > 0.000001) {
          this.markerPosition = { lat: newLat, lng: newLng };
          this.mapCenter = { lat: newLat, lng: newLng };

          // Update map view if map is loaded
          if (this.$refs.map && this.$refs.map.$mapPromise) {
            this.$refs.map.$mapPromise.then(map => {
              map.setCenter({ lat: newLat, lng: newLng });
              map.setZoom(15);
            }).catch(err => {
              console.log('Could not update map center:', err);
            });
          }
        }
      }
    },

    formatPhoneNumber(phone) {
      // Remove any non-digit characters
      if (phone.mobile_number) {
        phone.mobile_number = phone.mobile_number.replace(/\D/g, '');
        // Limit to 9 digits
        if (phone.mobile_number.length > 9) {
          phone.mobile_number = phone.mobile_number.slice(0, 9);
        }
      }
    },

    addPhoneNumber() {
      if (this.phoneNumbers.length < 2) {
        this.phoneNumbers.push({
          country_code: '+94',
          mobile_number: ''
        });
      }
    },

    removePhoneNumber(index) {
      this.phoneNumbers.splice(index, 1);
    },

    getCompanyOptions(index) {
      const currentValue = this.piyestraCompanies[index]?.company || '';
      const selectedInOtherRows = this.piyestraCompanies
        .map((company, rowIndex) => (rowIndex === index ? '' : company.company))
        .filter(Boolean);

      const filtered = this.availablePiyestraCompanies.filter(
        option => option === currentValue || !selectedInOtherRows.includes(option)
      );

      // Keep legacy/previously saved values visible in edit mode even if not in the hardcoded list.
      if (currentValue && !filtered.includes(currentValue)) {
        filtered.push(currentValue);
      }

      return filtered;
    },

    addCompany() {
      if (this.canAddCompany) {
        this.piyestraCompanies.push({
          company: '',
          credit_limit_type: '',
          credit_period_type: '',
          cash_limit_type: '',
          cash_period_type: '',
          rep_code: ''  // New companies will have empty rep_code
        });
      }
    },

    removeCompany(index) {
      this.piyestraCompanies.splice(index, 1);
    },

    async updateOutletData() {
      // Validate form basic fields
      const { valid } = await this.$refs.dealerForm.validate();
      if (!valid) {
        this.showErrorNotification('Please fill in all required fields');
        return;
      }

      if (!this.hasCompletePhoneNumbers) {
        this.showErrorNotification('Please add at least one valid mobile number and complete all phone number fields. Phone numbers must be exactly 9 digits.');
        return;
      }

      if (!this.hasCompleteCompanies) {
        this.showErrorNotification('Please add at least one company and complete all company detail fields.');
        return;
      }

      if (!this.hasLocation) {
        this.showErrorNotification('Please set the dealer location on the map before updating.');
        return;
      }

      this.isSaving = true;
      const token = localStorage.getItem('token');
      const dealerId = this.dealerId;

      if (!token) {
        this.showErrorNotification('Authentication token not found');
        this.isSaving = false;
        return;
      }

      // IMPORTANT: Include rep_code in the payload exactly as received from backend
      const normalizedCompanies = this.piyestraCompanies
        .map(company => {
          const normalized = {
            company: company.company ? company.company.trim() : '',
            credit_limit_type: company.credit_limit_type ? String(company.credit_limit_type).trim() : '0',
            credit_period_type: company.credit_period_type ? String(company.credit_period_type).trim() : '0',
            cash_limit_type: company.cash_limit_type ? String(company.cash_limit_type).trim() : '0',
            cash_period_type: company.cash_period_type ? String(company.cash_period_type).trim() : '0'
          };

          // Preserve the rep_code if it exists (from existing companies)
          if (company.rep_code) {
            normalized.rep_code = company.rep_code;
          }

          return normalized;
        });

      // Prepare payload according to API specification
      const payload = {
        dealer_name: this.formData.dealer_name.trim(),
        dealer_code: this.formData.dealer_code.trim(),
        company_name: this.formData.company_name.trim(),
        address: this.formData.address.trim(),
        region_code: this.formData.region_code.trim(),
        area_code: this.formData.area_code.trim(),
        dealer_type: this.formData.dealer_type, // NEW: Include dealer_type in payload
        mobile_number: this.phoneNumbers[0]?.mobile_number?.trim() || '',
        mobile_number2: this.phoneNumbers[1]?.mobile_number?.trim() || '',
        // Include companies with their rep_codes
        companies: normalizedCompanies,
        piyestra_company: normalizedCompanies
      };

      // Add location if marker exists
      if (this.markerPosition) {
        payload.lat = this.markerPosition.lat.toString();
        payload.lng = this.markerPosition.lng.toString();
      }

      console.log('Updating dealer with payload (including dealer_type and rep_code):', JSON.stringify(payload, null, 2));

      try {
        const response = await axios.put(
          `${this.baseURL}/api/updateDealer/${dealerId}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          this.showSuccessMessage('Dealer details updated successfully!');

          // Navigate back after success
          setTimeout(() => {
            this.$router.push('/dealer-list');
          }, 1500);
        } else {
          throw new Error(response.data?.message || 'Failed to update dealer');
        }
      } catch (error) {
        console.error("Error updating dealer data:", error);
        this.showErrorNotification(
          error.response?.data?.message ||
          error.message ||
          'Failed to update dealer details. Please try again.'
        );
      } finally {
        this.isSaving = false;
      }
    },

    cancelEdit() {
      this.$router.push('/dealer-list');
    },

    showErrorNotification(message) {
      this.errorMessage = message;
      this.showError = true;
    },

    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccess = true;
    }
  },

  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }
};
</script>

<style scoped>
@import "../../styles/outlet_management/outlet_list.scss";

.country-code-select {
  max-width: 100px;
}

.map-container {
  height: 300px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.gap-2 {
  gap: 8px;
}
</style>